/**
 * Inbox tab - inbound replies, grouped by intent.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Loader2, RefreshCw, Search, Check, RotateCcw, ExternalLink, Inbox,
} from 'lucide-react';

import type { ApiCall } from '../../api/interview';
import {
  type Reply,
  listReplies, getReply, markReplyHandled, reclassifyReply,
  triggerPollReplies,
} from '../../api/b2b';
import { Centered, ErrorBox, HeadingWithInfo, InfoTip, ActionWithInfo } from './_ui';


interface Props { apiCall: ApiCall; }


const INTENT_OPTIONS = [
  { id: '',             label: 'All' },
  { id: 'positive',     label: 'Positive' },
  { id: 'question',     label: 'Question' },
  { id: 'negative',     label: 'Negative' },
  { id: 'unsubscribe',  label: 'Unsubscribe' },
  { id: 'out_of_office',label: 'Out-of-office' },
  { id: 'other',        label: 'Other' },
];


const B2BTabInbox: React.FC<Props> = ({ apiCall }) => {
  const [replies, setReplies] = useState<Reply[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [intent, setIntent] = useState('');
  const [handled, setHandled] = useState<'all' | 'unhandled' | 'handled'>('unhandled');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [polling, setPolling] = useState(false);
  const [pollMsg, setPollMsg] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const handledParam =
        handled === 'unhandled' ? false :
        handled === 'handled'   ? true  :
        undefined;
      setReplies(await listReplies(apiCall, {
        intent: intent || undefined,
        handled: handledParam,
        search: search || undefined,
        limit: 100,
      }));
    } catch (e: any) {
      setError(e?.message || 'Failed to load replies');
    } finally {
      setLoading(false);
    }
  }, [apiCall, intent, handled, search]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
      <div className="lg:col-span-2 bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="p-3 border-b border-slate-100 space-y-2">
          <div className="flex gap-2 items-center">
            <HeadingWithInfo
              className="text-xs font-medium text-slate-600 shrink-0"
              info="Antworten auf versendete Emails - Intent per LLM (positive, unsubscribe, …)."
            >
              Replies
            </HeadingWithInfo>
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search from / subject / body"
                className="w-full pl-8 pr-2 py-1.5 text-sm border border-slate-200 rounded-md"
              />
            </div>
            <button onClick={load} className="p-1.5 border border-slate-200 rounded-md hover:bg-slate-50">
              <RefreshCw className="w-4 h-4 text-slate-600" />
            </button>
            <ActionWithInfo info="IMAP-Postfach jetzt abfragen - sonst stündlich automatisch. Unsubscribe → Blocklist.">
              <button
                onClick={async () => {
                  setPolling(true);
                  setPollMsg(null);
                  try {
                    const r = await triggerPollReplies(apiCall);
                    setPollMsg(`Poll started (${r.run_id.slice(0, 8)}…)`);
                    setTimeout(load, 8000);
                  } catch (e: any) {
                    setPollMsg(e?.message || 'Poll failed');
                  } finally {
                    setPolling(false);
                  }
                }}
                disabled={polling}
                className="inline-flex items-center gap-1 px-2 py-1.5 text-sm border border-slate-200 rounded-md hover:bg-slate-50 disabled:opacity-50"
              >
                {polling ? <Loader2 className="w-4 h-4 animate-spin" /> : <Inbox className="w-4 h-4" />}
                Poll IMAP
              </button>
            </ActionWithInfo>
          </div>
          {pollMsg && <div className="text-xs text-slate-500">{pollMsg}</div>}
          <div className="flex gap-2">
            <select value={intent} onChange={(e) => setIntent(e.target.value)}
                    className="flex-1 text-xs border border-slate-200 rounded-md px-2 py-1.5">
              {INTENT_OPTIONS.map((o) => (
                <option key={o.id} value={o.id}>{o.label}</option>
              ))}
            </select>
            <select value={handled} onChange={(e) => setHandled(e.target.value as any)}
                    className="flex-1 text-xs border border-slate-200 rounded-md px-2 py-1.5">
              <option value="unhandled">Unhandled</option>
              <option value="handled">Handled</option>
              <option value="all">All</option>
            </select>
          </div>
        </div>

        {loading ? (
          <Centered><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></Centered>
        ) : error ? (
          <ErrorBox message={error} onRetry={load} />
        ) : replies.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">
            No replies match. The IMAP poller pulls new replies hourly.
          </div>
        ) : (
          <ul className="divide-y divide-slate-100 max-h-[75vh] overflow-y-auto">
            {replies.map((r) => (
              <li
                key={r.id}
                onClick={() => setSelectedId(r.id)}
                className={`px-3 py-2 cursor-pointer hover:bg-slate-50 ${
                  selectedId === r.id ? 'bg-pink-50' : ''
                } ${r.handled ? 'opacity-60' : ''}`}
              >
                <div className="flex items-center justify-between gap-2 mb-0.5">
                  <span className="text-sm font-medium text-slate-800 truncate">
                    {r.from_email || '(no from)'}
                  </span>
                  <IntentBadge intent={r.intent} />
                </div>
                <div className="text-xs text-slate-600 truncate">{r.subject || '(no subject)'}</div>
                <div className="text-[10px] text-slate-400 mt-0.5">
                  {new Date(r.received_at).toLocaleString()}
                  {r.company_domain ? ` · ${r.company_domain}` : ''}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="lg:col-span-3">
        {selectedId == null ? (
          <div className="bg-white rounded-lg border border-slate-200 p-12 text-center text-slate-400 text-sm">
            Select a reply to view + handle
          </div>
        ) : (
          <ReplyDetailPanel
            apiCall={apiCall}
            replyId={selectedId}
            onChanged={load}
          />
        )}
      </div>
    </div>
  );
};

export default B2BTabInbox;


const IntentBadge: React.FC<{ intent: string | null }> = ({ intent }) => {
  const map: Record<string, string> = {
    positive:      'bg-emerald-100 text-emerald-700',
    question:      'bg-blue-100 text-blue-700',
    negative:      'bg-orange-100 text-orange-700',
    unsubscribe:   'bg-red-100 text-red-700',
    out_of_office: 'bg-slate-100 text-slate-500',
    other:         'bg-slate-100 text-slate-500',
  };
  const cls = map[intent || ''] || 'bg-slate-100 text-slate-500';
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded uppercase tracking-wider ${cls}`}>
      {intent || 'unknown'}
    </span>
  );
};


// ============================================================
// Detail panel
// ============================================================

const ReplyDetailPanel: React.FC<{
  apiCall: ApiCall;
  replyId: number;
  onChanged: () => void;
}> = ({ apiCall, replyId, onChanged }) => {
  const [reply, setReply] = useState<Reply | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setReply(await getReply(apiCall, replyId));
    } catch (e: any) {
      setError(e?.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [apiCall, replyId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="bg-white rounded-lg border border-slate-200 p-6"><Centered><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></Centered></div>;
  if (error)   return <ErrorBox message={error} onRetry={load} />;
  if (!reply)  return null;

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4 max-h-[80vh] overflow-y-auto space-y-3">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-medium text-slate-900 truncate">{reply.subject || '(no subject)'}</div>
          <div className="text-xs text-slate-500">
            From <span className="font-medium text-slate-700">{reply.from_email}</span>
            {reply.company_domain && ` · ${reply.company_domain}`}
          </div>
          <div className="text-[10px] text-slate-400">{new Date(reply.received_at).toLocaleString()}</div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <IntentBadge intent={reply.intent} />
          {reply.intent_confidence != null && (
            <span className="text-[10px] text-slate-400">{(reply.intent_confidence * 100).toFixed(0)}%</span>
          )}
        </div>
      </div>

      <div className="bg-slate-50 rounded p-3 text-sm text-slate-800 whitespace-pre-wrap max-h-[40vh] overflow-y-auto">
        {reply.body || '(empty body)'}
      </div>

      {reply.campaign_name && (
        <div className="text-xs text-slate-500">
          Campaign: <span className="font-medium text-slate-700">{reply.campaign_name}</span>
        </div>
      )}

      {/* Reclassify */}
      <div className="text-xs text-slate-500 flex flex-wrap items-center gap-1">
        <span className="inline-flex items-center gap-0.5">
          Reclassify as:
          <InfoTip text="Intent manuell korrigieren - z.B. unsubscribe setzt Contact auf DNC + Blocklist." align="left" />
        </span>
        {INTENT_OPTIONS.filter((o) => o.id !== '').map((o) => (
          <button
            key={o.id}
            disabled={busy || reply.intent === o.id}
            onClick={async () => {
              setBusy(true);
              try { await reclassifyReply(apiCall, reply.id, o.id); await load(); onChanged(); }
              finally { setBusy(false); }
            }}
            className={`px-1.5 py-0.5 rounded text-[10px] uppercase border ${
              reply.intent === o.id
                ? 'bg-slate-700 text-white border-slate-700'
                : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
            }`}
          >
            {o.label}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2">
        {!reply.handled ? (
          <ActionWithInfo info="Als bearbeitet markieren - verschwindet aus Unhandled-Filter.">
            <button
              onClick={async () => {
                setBusy(true);
                try { await markReplyHandled(apiCall, reply.id); await load(); onChanged(); }
                finally { setBusy(false); }
              }}
              disabled={busy}
              className="px-3 py-1.5 text-sm bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-1"
            >
              <Check className="w-4 h-4" /> Mark handled
            </button>
          </ActionWithInfo>
        ) : (
          <span className="text-xs text-slate-500 inline-flex items-center gap-1">
            <Check className="w-3.5 h-3.5 text-emerald-600" />
            Handled by {reply.handled_by} at {reply.handled_at && new Date(reply.handled_at).toLocaleString()}
          </span>
        )}
        {busy && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
      </div>
    </div>
  );
};
